import type { Brand } from "./types";

/**
 * 品牌主色覆盖层(设计稿 C-E)—— **不做第 4 套皮肤**,在当前皮肤之上只换色相/饱和度:
 *   - 亮度沿用当前皮肤(cyber 的 55% 亮青、standard 的 68% 蓝紫 …),皮肤的明暗节奏不被渠道色打乱;
 *   - 前景色(按钮字)按 WCAG 对比度在白/深之间自动挑;
 *   - hero 渐变保留皮肤原有的 from→to 色相差,只整体平移到品牌色相;
 *   - 皮肤私有调色板(霓虹青紫 / Stripe 蓝紫 / 漫画粉)+ accent 族一并换到品牌色相,
 *     主按钮渐变 = 品牌色明暗两档,侧栏选中 / 图标描边跟随(OEM F2)。
 * ★primaryColor 为空 = 什么都不做(直营永远走这条,零变化)。
 */

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** 覆盖层改写的皮肤变量。 */
export const OVERLAY_VARS = [
  "--primary",
  "--ring",
  "--sidebar-primary",
  "--sidebar-ring",
  "--gradient-hero-from",
  "--gradient-hero-to",
] as const;

/** accent 族:换色相,饱和度封顶皮肤原值,亮度/alpha 随皮肤。 */
export const ACCENT_VARS = [
  "--accent",
  "--accent-foreground",
  "--sidebar-accent",
  "--sidebar-accent-foreground",
] as const;

/**
 * 皮肤私有调色板(定义在 skin-*.css 的 `[data-skin=X]` 上,非当前皮肤读到空串 = 跳过)。
 * 覆盖层内联写在 <html> 上盖过 CSS;换皮前要先摘掉旧内联才能读到新皮肤原值。
 */
export const SKIN_PALETTE_VARS = [
  "--neon-cyan",
  "--neon-violet",
  "--stripe-blurple",
  "--stripe-blurple-deep",
  "--stripe-violet",
  "--comic-pink",
] as const;

/**
 * 覆盖层派生、CSS 里以 `var(--x, 原字面值)` 兜底消费的钩子(直营没人写它们 = 逐字原值)。
 * 图标描边渐变在 *.html 的 <linearGradient> 里用 `stop-color: var(--…, #原色)`。
 */
export const SKIN_HOOK_VARS = [
  "--neon-mid",
  "--neon-mid-soft",
  "--cyber-icon-stroke-from",
  "--cyber-icon-stroke-to",
  "--stripe-violet-deep",
  "--stripe-icon-stroke-from",
  "--stripe-icon-stroke-to",
  "--comic-pink-hover",
] as const;

/** 渐变搭档(无辅色时)比皮肤原亮度暗多少:品牌色的「明 → 暗」两档。 */
const PARTNER_DARKEN = 20;

const FOREGROUND_LIGHT = "0 0% 100%";
const FOREGROUND_DARK = "222 47% 11%";

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

function normHue(h: number) {
  return ((h % 360) + 360) % 360;
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: h * 60, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: Hsl): [number, number, number] {
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const k = (n: number) => (n + normHue(h) / 30) % 12;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n: number) => ln - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

/** 解析 CSS 变量里的 HSL 三元组("186 100% 55%" / "186 100% 55% / 0.3" / "hsl(186, 100%, 55%)")。 */
export function parseHslTriple(raw: string | null | undefined): Hsl | null {
  if (!raw) return null;
  const m = raw
    .trim()
    .replace(/^hsla?\(/i, "")
    .replace(/\)$/, "")
    .match(/^(-?[\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%/);
  if (!m) return null;
  return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) };
}

/** 解析品牌色:#rgb / #rrggbb / HSL 三元组。解析不了返回 null(= 不覆盖)。 */
export function parseBrandColor(raw: string | null | undefined): Hsl | null {
  if (!raw) return null;
  const v = raw.trim();
  const hex = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const full =
      hex[1].length === 3
        ? hex[1]
            .split("")
            .map((c) => c + c)
            .join("")
        : hex[1];
    const n = parseInt(full, 16);
    return rgbToHsl((n >> 16) & 255, (n >> 8) & 255, n & 255);
  }
  return parseHslTriple(v);
}

export function formatHsl({ h, s, l }: Hsl): string {
  const r = (n: number) => Math.round(n * 10) / 10;
  return `${r(normHue(h))} ${r(clamp(s, 0, 100))}% ${r(clamp(l, 0, 100))}%`;
}

export function toHex(c: Hsl): string {
  return (
    "#" +
    hslToRgb(c)
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
  );
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const ch = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/** WCAG 对比度(1~21)。 */
export function contrastRatio(a: Hsl, b: Hsl): number {
  const la = relativeLuminance(hslToRgb(a));
  const lb = relativeLuminance(hslToRgb(b));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** 按对比度挑前景:白 vs 深,取对比更高的那个。 */
export function pickForeground(bg: Hsl): string {
  const light = parseHslTriple(FOREGROUND_LIGHT)!;
  const dark = parseHslTriple(FOREGROUND_DARK)!;
  return contrastRatio(bg, light) >= contrastRatio(bg, dark) ? FOREGROUND_LIGHT : FOREGROUND_DARK;
}

/**
 * 纯计算:给定品牌色 + 当前皮肤的底值,算出要内联写入的变量表。
 * 底值缺失的变量跳过(不瞎编亮度)。
 */
export function computeBrandOverlay(
  brandColor: Hsl,
  base: Partial<Record<string, string>>,
  /** 品牌辅色(themeColorSecondary):给了就用它的色相/饱和度做 hero 渐变终点,亮度仍随皮肤。 */
  secondary: Hsl | null = null
): Record<string, string> {
  const out: Record<string, string> = {};
  const recolor = (v: Hsl, hueShift = 0): Hsl => ({
    h: brandColor.h + hueShift,
    s: brandColor.s,
    l: v.l,
  });

  for (const name of ["--primary", "--ring", "--sidebar-primary", "--sidebar-ring"] as const) {
    const b = parseHslTriple(base[name]);
    if (b) out[name] = formatHsl(recolor(b));
  }
  const from = parseHslTriple(base["--gradient-hero-from"]);
  const to = parseHslTriple(base["--gradient-hero-to"]);
  if (from) out["--gradient-hero-from"] = formatHsl(recolor(from));
  if (to) {
    out["--gradient-hero-to"] = secondary
      ? formatHsl({ h: secondary.h, s: secondary.s, l: to.l })
      : formatHsl(recolor(to, from ? to.h - from.h : 0));
  }

  const primary = parseHslTriple(out["--primary"]);
  if (primary) out["--primary-foreground"] = pickForeground(primary);
  const sidebarPrimary = parseHslTriple(out["--sidebar-primary"]);
  if (sidebarPrimary) out["--sidebar-primary-foreground"] = pickForeground(sidebarPrimary);

  // H5 钩子:wecom-light / advisor-dark 两套 data-theme 与 antd-mobile 取这几个。
  out["--brand-primary"] = formatHsl(brandColor);
  out["--brand-primary-hex"] = toHex(brandColor);
  out["--adm-color-primary"] = toHex(brandColor);
  if (secondary) {
    out["--brand-secondary"] = formatHsl(secondary);
    out["--brand-secondary-hex"] = toHex(secondary);
  }

  // accent 族(下拉/菜单悬停、侧栏选中底):换色相,饱和度不超过皮肤原值(淡底保持淡),亮度 + alpha 随皮肤。
  for (const name of ACCENT_VARS) {
    const raw = base[name];
    const b = parseHslTriple(raw);
    if (!b || !raw) continue;
    const alpha = raw.match(/\/\s*[\d.]+%?\s*$/)?.[0];
    const c = formatHsl({ h: brandColor.h, s: Math.min(b.s, brandColor.s), l: b.l });
    out[name] = alpha ? `${c} ${alpha.trim()}` : c;
  }

  // 皮肤私有调色板(skin-*.css 里写死的霓虹青紫 / Stripe 蓝紫 / 漫画粉):主色 = 品牌色相 + 皮肤亮度;
  // 渐变搭档 = 辅色(给了)或品牌色的暗一档 —— 登录页主按钮、后台主按钮、侧栏选中据此跟随品牌。
  const main = (b: Hsl): Hsl => ({ h: brandColor.h, s: brandColor.s, l: b.l });
  const partner = (l: number): Hsl => {
    const src = secondary ?? brandColor;
    return { h: src.h, s: src.s, l: secondary ? l : clamp(l - PARTNER_DARKEN, 20, 80) };
  };
  const cyan = parseHslTriple(base["--neon-cyan"]);
  if (cyan) {
    const c = main(cyan);
    const v = partner(parseHslTriple(base["--neon-violet"])?.l ?? 65);
    out["--neon-cyan"] = formatHsl(c);
    out["--neon-violet"] = formatHsl(v);
    out["--neon-mid"] = formatHsl({ ...c, l: 55 });
    out["--neon-mid-soft"] = formatHsl({ ...c, l: 70 });
    out["--cyber-icon-stroke-from"] = toHex(c);
    out["--cyber-icon-stroke-to"] = toHex(v);
  }
  const blurple = parseHslTriple(base["--stripe-blurple"]);
  if (blurple) {
    const c = main(blurple);
    const deep = main(parseHslTriple(base["--stripe-blurple-deep"]) ?? { ...blurple, l: 56 });
    const v = partner(parseHslTriple(base["--stripe-violet"])?.l ?? 72);
    out["--stripe-blurple"] = formatHsl(c);
    out["--stripe-blurple-deep"] = formatHsl(deep);
    out["--stripe-violet"] = formatHsl(v);
    out["--stripe-violet-deep"] = formatHsl(partner(60));
    out["--stripe-icon-stroke-from"] = toHex(c);
    out["--stripe-icon-stroke-to"] = toHex(v);
  }
  const pink = parseHslTriple(base["--comic-pink"]);
  if (pink) {
    out["--comic-pink"] = formatHsl(main(pink));
    out["--comic-pink-hover"] = formatHsl(main({ ...pink, l: 70 }));
  }
  return out;
}

function defaultReadBase(root: HTMLElement, name: string): string {
  const inline = root.style.getPropertyValue(name);
  if (inline) return inline;
  try {
    return getComputedStyle(root).getPropertyValue(name);
  } catch {
    return "";
  }
}

/**
 * 在当前皮肤上套品牌色。applyTheme 每次换皮后调一次;bootstrap 拿到品牌后调一次。
 * 幂等:色相/饱和度整体替换、亮度取皮肤值,重复调用结果不变。
 */
export function applyBrandOverlay(
  brand: Pick<Brand, "primaryColor"> & Partial<Pick<Brand, "secondaryColor">>,
  root: HTMLElement = document.documentElement,
  readBase: (root: HTMLElement, name: string) => string = defaultReadBase
): void {
  const color = parseBrandColor(brand.primaryColor);
  if (!color) return;
  // 渐变两色由 CSS [data-skin] 给,覆盖层写的是内联 —— 先摘掉旧内联,才能读到新皮肤的原值。
  root.style.removeProperty("--gradient-hero-from");
  root.style.removeProperty("--gradient-hero-to");
  // 皮肤私有调色板同理(由 [data-skin] 给);派生钩子没有 CSS 原值,摘掉 = 回到兜底字面值。
  for (const name of [...SKIN_PALETTE_VARS, ...SKIN_HOOK_VARS]) root.style.removeProperty(name);
  const base: Partial<Record<string, string>> = {};
  for (const name of [...OVERLAY_VARS, ...ACCENT_VARS, ...SKIN_PALETTE_VARS]) {
    base[name] = readBase(root, name);
  }
  const vars = computeBrandOverlay(color, base, parseBrandColor(brand.secondaryColor));
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
}
